"use client"

import { TrendingUp } from "lucide-react"
import { cn, formatPrice } from "@/lib/utils"

/**
 * Presentational parts of the offer sheet (`OfferModal`, an overlay on both
 * breakpoints). Everything here is TOKENS, not literals: the dark values are
 * byte-for-byte what they used to spell (`bg-muted` = `#262626` =
 * `bg-neutral-800`, `text-muted-foreground` = `#a3a3a3` = `text-neutral-400`),
 * so this is a visual no-op while the panel still forces `dark`, and it follows
 * the theme the moment it stops.
 *
 * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §1 (token arithmetic)
 * @see lib/creation-flow-form-styles.ts — the same rung table, applied to forms
 */
export const offerSheetTextareaClass =
  "w-full resize-none rounded-2xl border border-sheet-handle bg-muted px-4 py-3 text-base text-foreground placeholder-muted-foreground/70 focus:border-foreground/40 focus:outline-none focus:ring-2 focus:ring-foreground/40"

export const offerSheetSelectClass =
  "w-full rounded-lg border bg-background px-3 py-2 text-base"

export function OfferPercentChip({
  label,
  amountLabel,
  selected,
  onPress,
}: {
  label: string
  amountLabel: string
  selected: boolean
  onPress: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-pressed={selected}
      aria-label={`${label} ${amountLabel}`}
      className={cn(
        "flex flex-1 flex-col items-center gap-0.5 rounded-xl border-2 px-2 py-3 transition-colors",
        selected
          ? "border-foreground bg-foreground text-sheet"
          : "border-sheet-handle bg-transparent text-foreground hover:border-muted-foreground/70",
      )}
    >
      <span className="text-base font-extrabold">{label}</span>
      {/* Ink on the INVERTED (selected) chip, so it is the page/panel colour,
          not the foreground one. `text-background/70` over `bg-foreground` is
          `#525252` in dark — the old `text-neutral-600` byte for byte — and
          flips to a light grey on the near-black chip in the light theme. */}
      <span
        className={cn(
          "text-xs font-medium",
          selected ? "text-background/70" : "text-muted-foreground",
        )}
      >
        {amountLabel}
      </span>
    </button>
  )
}

export function OfferInfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-muted/60 p-4 text-sm font-medium leading-snug text-muted-foreground">
      {children}
    </div>
  )
}

export function OfferSavingsBadge({
  savingsLabel,
}: {
  savingsLabel: string
}) {
  return (
    <div className="mx-auto flex items-center gap-2 self-center rounded-full bg-emerald-500/15 px-4 py-2">
      <TrendingUp className="h-4 w-4 text-emerald-400" />
      <span className="text-sm font-bold text-emerald-400">{savingsLabel}</span>
    </div>
  )
}

export function formatOfferPriceLabel(
  amount: number,
  currency: string | null | undefined,
): string {
  return formatPrice(String(amount), currency ?? undefined)
}
